<template>
  <div>
    <div class="bg-gray-200 px-2 py-1 rounded-lg mb-2">
      <p class="text-sm font-light text-gray-600">
        Not sure what to select? <br />
        Leave it blank or find out more
        <UButton
          label="Support need A-Z"
          variant="ghost"
          icon="heroicons:information-circle-20-solid"
          size="sm"
          :ui="{
            padding: {
              sm: 'p-0'
            }
          }"
          class="underline ml-1 hover:text-blue-200 transition-colors duration-200"
          @click="isOpen = true"
        />
      </p>
    </div>
    <UInput
      v-model="search"
      placeholder="I need support with… e.g. stress"
      tabindex="12"
      :size="isNative ? 'xl' : 'lg'"
      icon="i-heroicons-magnifying-glass"
      :ui="{ icon: { trailing: { pointer: '' } } }"
      trailing
      aria-label="Search for support needs"
    ></UInput>
    <div v-if="[...firstNineCategories, ...remainingCategories].length">
      <!-- First nine SNC buttons -->
      <div class="flex flex-wrap gap-2 mt-4">
        <FrontendSupportNeedCategoryFilterInput
          v-for="category in firstNineCategories"
          :key="category.uid"
          v-model="filters.supportNeeds"
          :support-need-category-uid="category.uid"
          :support-need-category-display-text="category.displayText"
          :active="filters.supportNeeds.includes(category.uid)"
          :toggleable="toggleable"
          tabindex="0"
          role="checkbox"
          aria-label="Support need filter"
        />
      </div>

      <!-- Remaining SNC buttons -->
      <div v-if="showingAllCategories" class="flex flex-wrap gap-2 mt-2">
        <FrontendSupportNeedCategoryFilterInput
          v-for="category in remainingCategories"
          :key="category.uid"
          v-model="filters.supportNeeds"
          :support-need-category-uid="category.uid"
          :support-need-category-display-text="category.displayText"
          :active="filters.supportNeeds.includes(category.uid)"
          :toggleable="toggleable"
          tabindex="0"
          role="checkbox"
        />
      </div>

      <!-- Show more button -->
      <div v-if="remainingCategories.length" class="w-full flex items-center gap-2 mt-2">
        <!-- Indicator to show amount more categories selected when remaining categories hidden from view -->
        <span
          v-if="selectedRemainingCategories.length && !showingAllCategories"
          class="text-blue text-xs font-bold"
        >
          <span v-if="selectedFistNineCategories.length">+</span>
          {{ selectedRemainingCategories.length }}
        </span>
        <UButton
          :label="showingAllCategories ? 'Show less' : 'Show more'"
          variant="ghost"
          :icon="showingAllCategories ? 'mdi:triangle' : 'mdi:triangle-down'"
          trailing
          size="sm"
          :ui="{
            padding: {
              sm: 'p-0'
            }
          }"
          class="underline ml-1 hover:text-blue-200 transition-colors duration-200"
          @click="showingAllCategories = !showingAllCategories"
        />
      </div>
    </div>
    <p v-else class="text-sm text-gray-600 mt-8">
      No matching support needs found. <br />
      Try searching for something else or check other filters below e.g. Preferences or Age Category
    </p>
  </div>
</template>
<script setup lang="ts">
import type { SupportNeedCategory } from '~/types/SupportNeedCategory'
import { youthAgeRange } from '~/composables/useAgeBrackets'

// Get service search store
const serviceSearchStore = useServiceSearchStore()
const { filters } = storeToRefs(serviceSearchStore)

// Get frontend support need category search store
const frontendSupportNeedCategorySearchStore = useSupportNeedCategoryStore()

const { isNative } = useRuntimeConfig().public

// Props
const props = defineProps({
  supportNeedCategories: {
    type: Array as PropType<SupportNeedCategory[]>,
    required: false,
    default: () => []
  },
  toggleable: Boolean
})

const { disabledFilters } = storeToRefs(useServiceSearchStore())

// Filter out support needs from search term
const search = ref('')
const searchedCategories = computed(() => {
  let searched = frontendSupportNeedCategorySearchStore.filterCategoriesBySearchTerm(search.value)
  // filter out any disabled sncs if needed
  // if we're in toggleable mode, return all - this is admin mode
  if (props.toggleable) return searched
  // otherwise, filter out any that are disabled for youth if we're in youth mode
  const filtersMaxAge = filters.value.ageRange[1]
  const youthMaxAge = youthAgeRange[1]
  if (filtersMaxAge <= youthMaxAge) {
    searched = searched.filter((x) => x.youngPersonFriendly)
  }
  // and filter out any disabled ones
  const disabled = disabledFilters.value
  return searched.filter((x) => !disabled.includes(`support-needs:${x.uid}`))
})

const showingAllCategories = ref(false)
const firstNineCategories = computed(() => searchedCategories.value.slice(0, 9))
const remainingCategories = computed(() => searchedCategories.value.slice(9))

const selectedFistNineCategories = computed(() =>
  firstNineCategories.value.filter((category) => filters.value.supportNeeds.includes(category.uid))
)
const selectedRemainingCategories = computed(() =>
  remainingCategories.value.filter((category) => filters.value.supportNeeds.includes(category.uid))
)

const supportNeedGlossaryStore = useSupportNeedGlossaryStore()
const { isOpen } = storeToRefs(supportNeedGlossaryStore)
</script>
